import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import axios from "axios";
import { useDispatch, useSelector } from "react-redux";
import { user } from "../store/modules/UserStore";
import { useTranslation } from "react-i18next";
import { getLanguage, getParameter } from "../store/modules/RequestStore";

// Crea el contexto de traducción
const TranslationContext = createContext();

export const useTranslationContext = () => {
  return useContext(TranslationContext);
};

// Proveedor del contexto de traducción
export const TranslationProvider = ({ children }) => {
  const dispatch = useDispatch();
  const userDetails = useSelector(user);
  const { i18n } = useTranslation();

  const [targetLang, setTargetLang] = useState("es"); // Idioma destino por defecto
  const [isLoading, setIsLoading] = useState(false);
  const [activeLang, setActiveLang] = useState(false);
  const [error, setError] = useState(null);
  const [apiGoogle, setApiGoogle] = useState(
    process.env.REACT_APP_API_TRANSLATE
  );

  const urlMulti = process.env.REACT_APP_API_MULTI_PROD;

  const defaultLang = "es";

  // Función para detectar el idioma del navegador
  const getBrowserLanguage = () => {
    const language = navigator.language || navigator.languages[0];
    return language.split("-")[0]; // Devuelve solo el código del idioma (ej: "es", "en", etc.)
  };

  const setLanguage = useCallback(
    (lang) => {
      setTargetLang(lang);
      i18n.changeLanguage(lang);
      localStorage.setItem("selectedLanguage", lang);
    },
    [i18n]
  );

  useEffect(() => {
    const fetchParameter = async () => {
      const paramShowLang = await getParameter({ parameterName: "verIdioma" });
      if (
        paramShowLang?.data?.status === "Ok" &&
        paramShowLang.data.values.parameterValue.toString() === "1"
      ) {
        setActiveLang(true);
      } else {
        setActiveLang(false);
      }
    };

    fetchParameter();
  }, []);

  useEffect(() => {
    const browserLang = getBrowserLanguage();
    const usernameLS = localStorage.getItem("userName");

    const userName =
        userDetails.username !== ""
          ? userDetails.username
          : usernameLS?.replace(/"/g, "");

    const fetchLang = async () => {
      const lang = await dispatch(getLanguage(userName));

      if (lang?.data?.values && lang?.data?.values.userLang !== null) {
        let langValue = lang.data.values.userLang;
        setLanguage(langValue);
      } else {
        setLanguage(browserLang);
      }
    };

    // Si el parametro está activo y el username existe
    if (userName && activeLang) {
      fetchLang();
    } else {
      setLanguage(defaultLang);
    }
  }, [i18n, userDetails.username, activeLang]);

  useEffect(() => {
    const getApiKeyGoogle = async () => {
      try {
        const response = await axios.post(
          `${urlMulti}/geminiApi/getDataConnection`,
          {
            idSolution: 10,
          }
        );
        setApiGoogle(response.data[0]?.sol_api_key);
        // setApiGoogle('AIzaSyD51U_NUhQuBi3ujPIbG1PbVmN6_PKDfxs')
      } catch (error) {
        console.log(error);
      }
    };
    getApiKeyGoogle();
  }, []);

  // Función para traducir texto usando Google Cloud Translation API
  const translateText = async (text, format) => {
    const url = `https://translation.googleapis.com/language/translate/v2?key=${apiGoogle}`;
    setIsLoading(false);

    try {
      const response = await axios.post(url, {
        q: text,
        target: targetLang,
        format: format || "text",
        source: "es"
      });

      const translated = response.data.data.translations[0].translatedText;
      setIsLoading(false);
      return translated;
    } catch (err) {
      setError(err.message);
      setIsLoading(false);
      return text; // Devuelve el texto original en caso de error
    }
  };

  // Función para traducir texto en HTML
  const translateHTML = async (html) => {
    const url = `https://translation.googleapis.com/language/translate/v2?key=${apiGoogle}`;
    setIsLoading(true);

    try {
      const response = await axios.post(url, {
        q: html,
        target: targetLang,
        format: "html", // Especificamos el formato HTML
      });

      const translated = response.data.data.translations[0].translatedText;
      setIsLoading(false);
      return translated;
    } catch (err) {
      setError(err.message);
      setIsLoading(false);
      return html;
    }
  };

  // Función para cambiar el idioma
  const changeLanguage = (lang) => {
    setTargetLang(lang);
    localStorage.setItem("selectedLanguage", lang); // Guardar el idioma en localStorage
  };

  // Proveemos las funciones y estados necesarios a través del contexto
  return (
    <TranslationContext.Provider
      value={{
        translateText,
        translateHTML,
        changeLanguage,
        isLoading,
        error,
        targetLang,
      }}
    >
      {children}
    </TranslationContext.Provider>
  );
};
